import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mail, Phone, MessageSquare, User as UserIcon, Building2, Calendar,
  Inbox, ArrowRight, Trash2,
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "new", label: "New", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
  { value: "contacted", label: "Contacted", cls: "bg-[#FEF3C7] text-[#92400E]" },
  { value: "converted", label: "Enrolled", cls: "bg-[#DCFCE7] text-[#166534]" },
  { value: "closed", label: "Closed", cls: "bg-[#F1F5F9] text-[#475569]" },
];

function StatusPill({ status }) {
  const cfg = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>{cfg.label}</span>;
}

export default function InquiriesPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await api.get("/inquiries", { params });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const open = items.find((i) => i.id === openId) || null;

  async function changeStatus(id, status) {
    setSavingId(id);
    try {
      await api.patch(`/inquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingId(null); }
  }

  async function saveNotes(id, notes) {
    setSavingId(id);
    try {
      await api.patch(`/inquiries/${id}`, { internal_notes: notes });
      toast.success("Notes saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingId(null); }
  }

  async function removeInquiry(id) {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      toast.success("Inquiry deleted");
      setOpenId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s.value] = items.filter((i) => i.status === s.value).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground tracking-tight">Inquiries</h1>
          <p className="text-slate-500 mt-1">
            Application leads submitted through your public school landing page.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FilterPill label="All" value="all" filter={filter} setFilter={setFilter} count={items.length} />
          {STATUS_OPTIONS.map((s) => (
            <FilterPill key={s.value} label={s.label} value={s.value} filter={filter} setFilter={setFilter} count={counts[s.value] || 0} />
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center" data-testid="inquiries-empty">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#EFF6FF] flex items-center justify-center mb-3">
              <Inbox className="w-8 h-8 text-[#2563EB]" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">No inquiries yet</p>
            <p className="text-slate-500 mt-1 max-w-md mx-auto">
              When parents tap &quot;Apply Now&quot; on your public school landing page, their submissions will show up here.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#2563EB]/10">
            {items.map((row) => (
              <li
                key={row.id}
                className="px-4 sm:px-6 py-4 hover:bg-[#F8FAFC] transition-colors cursor-pointer"
                onClick={() => setOpenId(row.id)}
                data-testid={`inquiry-row-${row.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center shrink-0">
                    <UserIcon className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground truncate">{row.inquirer_name}</span>
                      <StatusPill status={row.status} />
                      {row.branch_name && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <Building2 className="w-3 h-3" /> {row.branch_name}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                      <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {row.inquirer_email}</span>
                      {row.inquirer_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {row.inquirer_phone}</span>}
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {(row.created_at || "").slice(0, 10)}</span>
                    </div>
                    {row.student_name && (
                      <p className="text-xs text-slate-600 mt-1">
                        Student: <span className="font-medium">{row.student_name}</span>{row.student_age && ` (age ${row.student_age})`}
                      </p>
                    )}
                    {row.message && (
                      <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{row.message}</p>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 mt-2 shrink-0" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <InquiryDetailDialog
        inquiry={open}
        onOpenChange={(v) => !v && setOpenId(null)}
        onStatus={changeStatus}
        onSaveNotes={saveNotes}
        onDelete={isOwner ? removeInquiry : null}
        saving={!!savingId}
      />
    </div>
  );
}

function FilterPill({ label, value, filter, setFilter, count }) {
  const active = filter === value;
  return (
    <button
      onClick={() => setFilter(value)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-white text-slate-600 border-slate-200 hover:border-[#2563EB]/30"}`}
      data-testid={`inquiry-filter-${value}`}
    >
      {label} <span className="ml-1 opacity-70">{count}</span>
    </button>
  );
}

function InquiryDetailDialog({ inquiry, onOpenChange, onStatus, onSaveNotes, onDelete, saving }) {
  const [notes, setNotes] = useState("");
  useEffect(() => { setNotes(inquiry?.internal_notes || ""); }, [inquiry?.id, inquiry?.internal_notes]);

  if (!inquiry) return null;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="inquiry-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-[#2563EB]" />
            {inquiry.inquirer_name}
          </DialogTitle>
          <DialogDescription>
            {(inquiry.created_at || "").slice(0, 16).replace("T", " · ")} {inquiry.branch_name && `· ${inquiry.branch_name}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <a href={`mailto:${inquiry.inquirer_email}`} className="rounded-2xl border border-slate-200 p-3 hover:border-[#2563EB]/30 transition-colors" data-testid="inquiry-email-link">
              <div className="text-xs text-slate-500">Email</div>
              <div className="font-semibold text-[#1D4ED8] flex items-center gap-1.5 mt-0.5"><Mail className="w-4 h-4" /> {inquiry.inquirer_email}</div>
            </a>
            {inquiry.inquirer_phone && (
              <a href={`tel:${inquiry.inquirer_phone}`} className="rounded-2xl border border-slate-200 p-3 hover:border-[#2563EB]/30 transition-colors" data-testid="inquiry-phone-link">
                <div className="text-xs text-slate-500">Phone</div>
                <div className="font-semibold text-[#1D4ED8] flex items-center gap-1.5 mt-0.5"><Phone className="w-4 h-4" /> {inquiry.inquirer_phone}</div>
              </a>
            )}
          </div>

          {(inquiry.student_name || inquiry.student_age) && (
            <div className="rounded-2xl bg-[#EFF6FF] p-3 text-sm">
              <div className="text-xs text-slate-500 mb-0.5">Prospective student</div>
              <div className="font-semibold text-foreground">
                {inquiry.student_name || "—"}{inquiry.student_age && ` · age ${inquiry.student_age}`}
              </div>
            </div>
          )}

          {inquiry.message && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Message</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-2xl p-3">{inquiry.message}</p>
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</Label>
            <Select value={inquiry.status} onValueChange={(v) => onStatus(inquiry.id, v)}>
              <SelectTrigger className="mt-1" data-testid="inquiry-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="inq-notes" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Internal notes</Label>
            <Textarea
              id="inq-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add follow-up notes, next steps, etc."
              rows={3}
              className="mt-1"
              maxLength={2000}
              data-testid="inquiry-notes-input"
            />
            <Button
              size="sm" variant="outline"
              className="mt-2"
              disabled={saving || notes === (inquiry.internal_notes || "")}
              onClick={() => onSaveNotes(inquiry.id, notes)}
              data-testid="inquiry-notes-save"
            >Save notes</Button>
          </div>
        </div>

        <DialogFooter className="flex sm:justify-between">
          {onDelete && (
            <Button
              variant="ghost"
              className="text-red-600 hover:bg-red-50"
              onClick={() => onDelete(inquiry.id)}
              data-testid="inquiry-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} data-testid="inquiry-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
